import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Circuit } from '../../../../../api/api-device';
import { selectAssignments } from '../../../system-details/systemDetailsSlice';
import {
  selectAllAppliances,
  selectAllCircuits,
  setAllCircuits,
  updateAppliance,
} from '../circuitApplianceConfigSlice';

export const useUpdateCircuit = (circuit: Circuit) => {
  const dispatch = useDispatch();
  const allCircuits = useSelector(selectAllCircuits);
  const allAppliances = useSelector(selectAllAppliances);
  const assignments = useSelector(selectAssignments);

  const shortenedCircuitId = useMemo(() => {
    const oemCircuitId = circuit.oem_circuit_id;
    const shortenedId = oemCircuitId?.split('_').pop(); // get the last part of the circuit id for easier reading

    return shortenedId ?? oemCircuitId;
  }, [circuit.oem_circuit_id]);

  const getNewCircuitName = (newMonitor: string) => {
    // update circuit name if necessary
    let newCircuitName = circuit.circuit_name;

    // if circuit name is not Not Set, it means it was set before and user could be just playing around
    // Hence ops team has requested that we do not change the circuit name in that case
    const isPreviouslyNotSet = circuit.clipsal_monitors === 'not_set' && circuit.circuit_name === 'Not Set';

    // If the monitor was previously not set, and the new monitor is not 'not_set',
    // we need to set the circuit name to the assignment display name
    if (isPreviouslyNotSet && newMonitor !== 'not_set') {
      const assignment = assignments.find((a) => a.assignment === newMonitor);
      newCircuitName = assignment?.display_name ?? circuit.circuit_name;
    }

    return newCircuitName;
  };

  const handleSelectMonitor = (newMonitor: string) => {
    // check if new monitor aligns with appliance type
    const appliance = allAppliances.find((a) => a.appliance_id === circuit.appliance_id);
    const hasApplianceTypeChanged = appliance && appliance.appliance_type !== newMonitor;
    const isTheOnlyMonitorInAppliance = (appliance?.circuits.length ?? 0) <= 1;
    const hasAssignmentChanged = circuit.clipsal_monitors !== newMonitor;
    const circuitName = getNewCircuitName(newMonitor);

    if (hasAssignmentChanged) {
      dispatch(
        setAllCircuits(
          allCircuits.map((c) =>
            c.oem_circuit_id === circuit.oem_circuit_id
              ? {
                  ...c,
                  clipsal_monitors: newMonitor,
                  circuit_name: circuitName,
                }
              : c
          )
        )
      );
    }

    // If the appliance type has changed and this is the only monitor in the appliance,
    // we need to update the appliance name and type
    if (hasApplianceTypeChanged && isTheOnlyMonitorInAppliance) {
      const assignment = assignments.find((a) => a.assignment === newMonitor);
      dispatch(
        updateAppliance({
          applianceId: appliance.appliance_id,
          name: assignment?.display_name ?? circuitName,
          type: newMonitor,
        })
      );
    }
  };

  const handleChangeCircuitName = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      setAllCircuits(
        allCircuits.map((c) =>
          c.oem_circuit_id === circuit.oem_circuit_id ? { ...c, circuit_name: e.target.value } : c
        )
      )
    );
  };

  const handleSelectControlledLoad = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      setAllCircuits(
        allCircuits.map((c) =>
          c.oem_circuit_id === circuit.oem_circuit_id
            ? { ...c, controlled_load_yn: e.target.value as Circuit['controlled_load_yn'] }
            : c
        )
      )
    );
  };
  const handleSelectControlledLoad2 = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(
      setAllCircuits(
        allCircuits.map((c) =>
          c.oem_circuit_id === circuit.oem_circuit_id
            ? { ...c, controlled_load2_yn: e.target.value as Circuit['controlled_load2_yn'] }
            : c
        )
      )
    );
  };

  return {
    shortenedCircuitId,
    handleSelectMonitor,
    handleChangeCircuitName,
    handleSelectControlledLoad,
    handleSelectControlledLoad2,
  };
};
